// Removed Sentry service - using Elasticsearch for analytics tracking

interface AnalyticsEvent {
  event: string;
  properties?: Record<string, any>;
  timestamp: number;
  userId?: string;
  sessionId: string;
  url: string;
  userAgent: string;
}

interface UserProperties {
  userId?: string;
  email?: string;
  role?: string;
  signupDate?: string;
  lastLogin?: string;
  [key: string]: any;
}

interface AnalyticsConfig {
  enableConsoleLogging?: boolean;
  enableLocalStorage?: boolean;
  batchSize?: number;
  flushInterval?: number;
  customEndpoint?: string;
}

class AnalyticsService {
  private config: AnalyticsConfig = {
    enableConsoleLogging: false,
    enableLocalStorage: true,
    batchSize: 50,
    flushInterval: 30000, // 30 seconds
  };
  
  private events: AnalyticsEvent[] = [];
  private sessionId: string;
  private userId?: string;
  private userProperties: UserProperties = {};
  private flushTimer?: number;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.setupPerformanceListener();
    this.setupPageVisibilityListener();
  }

  initialize(config?: Partial<AnalyticsConfig>): void {
    this.config = { ...this.config, ...config };
    this.loadFromLocalStorage();
    
    // Set up auto-flush timer
    if (this.config.flushInterval && this.config.flushInterval > 0) {
      this.flushTimer = window.setInterval(() => {
        this.flush();
      }, this.config.flushInterval);
    }
  }

  track(event: string, properties?: Record<string, any>): void {
    const analyticsEvent: AnalyticsEvent = {
      event,
      properties: {
        ...this.userProperties,
        ...properties,
      },
      timestamp: Date.now(),
      userId: this.userId,
      sessionId: this.sessionId,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    this.events.push(analyticsEvent);

    if (this.config.enableConsoleLogging && !import.meta.env.PROD) {
      console.log('Analytics Event:', analyticsEvent);
    }

    // Sentry tracking removed - using Elasticsearch instead

    // Store in localStorage if enabled
    if (this.config.enableLocalStorage) {
      this.saveToLocalStorage();
    }

    // Auto-flush if batch size is reached
    if (this.events.length >= (this.config.batchSize || 50)) {
      this.flush();
    }
  }

  identify(userId: string, properties?: UserProperties): void {
    this.userId = userId;
    this.userProperties = { ...this.userProperties, ...properties, userId };

    // Sentry user tracking removed - using Elasticsearch instead

    this.track('user_identified', { userId, ...properties });
  }

  setUserProperties(properties: UserProperties): void {
    this.userProperties = { ...this.userProperties, ...properties };
  }

  trackFeatureUsage(feature: string, action: string, properties?: Record<string, any>): void {
    this.track('feature_usage', {
      feature,
      action,
      ...properties,
    });
  }

  trackError(error: Error, context?: Record<string, any>): void {
    this.track('error', {
      errorName: error.name,
      errorMessage: error.message,
      errorStack: error.stack,
      ...context,
    });
  }

  trackPageView(path?: string): void {
    this.track('page_view', {
      path: path || window.location.pathname,
      referrer: document.referrer,
      title: document.title,
    });
  }

  trackUserAction(action: string, target?: string, properties?: Record<string, any>): void {
    this.track('user_action', {
      action,
      target,
      ...properties,
    });
  }

  flush(): void {
    if (this.events.length === 0) return;

    const batch = [...this.events];
    this.events = [];

    if (this.config.enableConsoleLogging && !import.meta.env.PROD) {
      console.log('Analytics Batch Flush:', { batchSize: batch.length, events: batch });
    }

    // Send to custom endpoint if configured
    if (this.config.customEndpoint) {
      this.sendToEndpoint(batch);
    }

    // Clear from localStorage
    if (this.config.enableLocalStorage) {
      localStorage.removeItem('tesseract_analytics_events');
    }

    // Sentry batch logging removed - using Elasticsearch instead
  }

  private setupPerformanceListener(): void {
    // Listen for performance metrics from performanceService
    window.addEventListener('performance-metric', (event) => {
      const metric = (event as CustomEvent).detail;
      this.track('performance_metric', {
        metricName: metric.name,
        metricValue: metric.value,
        metricRating: metric.rating,
      });
    });
  }

  private setupPageVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.track('page_hidden');
        this.flush(); // Flush events when page becomes hidden
      } else {
        this.track('page_visible');
      }
    });

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flush();
    });
  }

  private async sendToEndpoint(events: AnalyticsEvent[]): Promise<void> {
    if (!this.config.customEndpoint) return;

    try {
      await fetch(this.config.customEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ events }),
      });
    } catch (error) {
      console.warn('Failed to send analytics to endpoint:', error);
      
      // Re-add events to the queue if sending failed
      this.events.unshift(...events);
    }
  }

  private saveToLocalStorage(): void {
    if (!this.config.enableLocalStorage) return;

    try {
      localStorage.setItem('tesseract_analytics_events', JSON.stringify(this.events));
    } catch (error) {
      console.warn('Failed to save analytics events to localStorage:', error);
    }
  }

  private loadFromLocalStorage(): void {
    if (!this.config.enableLocalStorage) return;

    try {
      const stored = localStorage.getItem('tesseract_analytics_events');
      if (stored) {
        this.events = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load analytics events from localStorage:', error);
    }
  }

  private generateSessionId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  // Getters for debugging and monitoring
  getSessionId(): string {
    return this.sessionId;
  }

  getUserId(): string | undefined {
    return this.userId;
  }

  getEventCount(): number {
    return this.events.length;
  }

  getEvents(): AnalyticsEvent[] {
    return [...this.events];
  }

  clearEvents(): void {
    this.events = [];
    if (this.config.enableLocalStorage) {
      localStorage.removeItem('tesseract_analytics_events');
    }
  }

  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush();
  }
}

export const analyticsService = new AnalyticsService();